import { ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';

interface ModalProps {
  title: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** 宽弹窗（导入 / 预检结果用）。 */
  wide?: boolean;
}

/**
 * 轻量受控弹窗。
 *
 * 架构红线（Agent.md 6.2）：Dialog/Modal 必须通过 `key` 触发重挂载以重置内部
 * 表单状态——调用方在条件渲染该弹窗时务必传入与目标实体绑定的 `key`
 * （如 `key={`edit-${course.id}`}`），切换目标时强制重建表单。
 */
export function Modal({ title, description, onClose, children, footer, wide }: ModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        // 仅点击遮罩层（而非内容）时关闭。
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={clsx(
          'mt-6 w-full rounded-2xl bg-white p-5 shadow-xl ring-1 ring-slate-200',
          wide ? 'max-w-2xl' : 'max-w-md',
        )}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-slate-900">{title}</div>
            {description ? (
              <p className="mt-0.5 text-xs text-slate-500">{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
        {footer ? (
          <div className="mt-5 flex items-center justify-end gap-2">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}
